
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';

interface BeforeAfterMessagesModel {
    title?: string;
    welcomeMessage: string;
    purchaseMessage: string;
}

@Component
export default class BeforeAfterMessages extends Vue {
    @Prop({
        type: Boolean,
        required: true,
    })
    public value!: boolean;

    @Prop({
        type: Object,
        required: false,
        default: () => ({
            title: '',
            welcomeMessage: '',
            purchaseMessage: '',
        }),
    })
    public messages!: BeforeAfterMessagesModel;

    @Prop({
        type: Boolean,
        required: false,
        default: false,
    })
    public canDelete!: boolean;

    public currentTab: number | string = 0;
    public templateName = '';
    public localWelcomeMessage = '';
    public localPurchaseMessage = '';
    public toolbarState = {
        bold: false,
        italic: false,
        underline: false,
        large: false,
        small: false,
    };
    public templateNameErrors: string[] = [];
    public welcomeMessageError = '';
    public purchaseMessageError = '';
    private activeFontSize: '4' | '7' | null = null;
    public readonly maxChars = 5000;
    private lastValidWelcomeHtml = '';
    private lastValidPurchaseHtml = '';
    private initFocusRafId: number | null = null;
    private lastActiveEditor: HTMLDivElement | null = null;

    public mounted() {
        this.enableCssMode();
        this.setLocalValues();
        this.$nextTick(() => {
            this.syncEditorsFromModel();
            this.focusActiveEditor();
            this.saveSelection();
            this.refreshToolbarState();
            this.saveLastValidFromEditors();
        });
    }

    public beforeDestroy() {
        this.clearInitFocusRaf();
    }

    @Watch('value')
    public onValueChange(v: boolean) {
        if (v) {
            this.clearValidationErrors();
            this.currentTab = 0;
            this.setLocalValues();
            this.$nextTick(() => {
                this.initializeActiveEditorState();
            });
        } else {
            this.currentTab = 0;
        }
    }

    @Watch('messages', { deep: true })
    public onMessagesChange() {
        if (this.value) {
            this.setLocalValues();
            this.$nextTick(() => this.syncEditorsFromModel());
        }
    }

    @Watch('currentTab')
    public onTabChange() {
        this.lastActiveEditor = null;
        this.$nextTick(() => {
            this.initializeActiveEditorState();
        });
    }

    @Watch('templateName')
    public onTemplateNameChanged() {
        if (this.templateName.trim()) {
            this.templateNameErrors = [];
        }
    }

    public onDialogInput(v: boolean) {
        if (!v) {
            this.currentTab = 0;
            this.clearInitFocusRaf();
            this.lastActiveEditor = null;
        }
        this.$emit('input', v);
    }

    public close() {
        this.clearInitFocusRaf();
        this.lastActiveEditor = null;
        this.clearValidationErrors();
        this.$emit('input', false);
    }

    public save() {
        this.syncModelFromEditors();
        if (!this.validate()) {
            return;
        }

        this.$emit('save', {
            title: this.templateName.trim(),
            welcomeMessage: this.localWelcomeMessage,
            purchaseMessage: this.localPurchaseMessage,
        });
    }

    public onTrashClick() {
        if (this.canDelete) {
            this.$emit('delete');
            return;
        }

        this.clearMessages();
    }

    public clearMessages() {
        this.localWelcomeMessage = '';
        this.localPurchaseMessage = '';
        this.syncEditorsFromModel();
        this.activeFontSize = null;
        this.toolbarState.large = false;
        this.toolbarState.small = false;
        this.refreshToolbarState();
        this.saveLastValidFromEditors();
    }

    private setLocalValues() {
        this.templateName = (this.messages?.title || '').trim();
        this.localWelcomeMessage = this.sanitizeHtml(this.messages?.welcomeMessage || '');
        this.localPurchaseMessage = this.sanitizeHtml(this.messages?.purchaseMessage || '');
    }

    private validate() {
        const title = this.templateName.trim();
        const welcomeText = this.htmlToPlainText(this.localWelcomeMessage).trim();
        const purchaseText = this.htmlToPlainText(this.localPurchaseMessage).trim();
        this.clearValidationErrors();

        if (!title) {
            this.templateNameErrors = ['Podaj nazwę szablonu.'];
        }
        if (!welcomeText) {
            this.welcomeMessageError = 'Wpisz wiadomość powitalną.';
        }
        if (!purchaseText) {
            this.purchaseMessageError = 'Wpisz wiadomość po zakupie.';
        }

        if (this.welcomeMessageError) {
            this.currentTab = 0;
        } else if (this.purchaseMessageError) {
            this.currentTab = 1;
        }

        return !this.templateNameErrors.length && !this.welcomeMessageError && !this.purchaseMessageError;
    }

    get currentCharCount() {
        const html = this.currentTab === 0 ? this.localWelcomeMessage : this.localPurchaseMessage;
        return this.htmlToPlainText(html).length;
    }

    private get activeEditor(): HTMLDivElement | null {
        const root = this.$el as HTMLElement | undefined;
        if (root) {
            const activeByDom = root.querySelector('.v-window-item--active .rich-editor') as HTMLDivElement | null;
            if (activeByDom) {
                return activeByDom;
            }
        }

        if (this.lastActiveEditor) {
            return this.lastActiveEditor;
        }

        const welcomeEditor = (this.$refs.welcomeEditor as HTMLDivElement | undefined) || null;
        const purchaseEditor = (this.$refs.purchaseEditor as HTMLDivElement | undefined) || null;

        return this.isPurchaseTab() ? purchaseEditor : welcomeEditor;
    }

    private enableCssMode() {
        try {
            document.execCommand('styleWithCSS', false, 'false');
        } catch (_) {}
    }

    private syncEditorsFromModel() {
        const welcome = (this.$refs.welcomeEditor as HTMLDivElement | undefined) || null;
        const purchase = (this.$refs.purchaseEditor as HTMLDivElement | undefined) || null;

        if (welcome) {
            welcome.innerHTML = this.sanitizeHtml(this.localWelcomeMessage || '');
        }
        if (purchase) {
            purchase.innerHTML = this.sanitizeHtml(this.localPurchaseMessage || '');
        }

        this.saveLastValidFromEditors();
    }

    private syncModelFromEditors() {
        const welcome = (this.$refs.welcomeEditor as HTMLDivElement | undefined) || null;
        const purchase = (this.$refs.purchaseEditor as HTMLDivElement | undefined) || null;

        this.localWelcomeMessage = welcome ? this.sanitizeHtml(welcome.innerHTML) : this.localWelcomeMessage;
        this.localPurchaseMessage = purchase ? this.sanitizeHtml(purchase.innerHTML) : this.localPurchaseMessage;
    }

    public onEditorInput(event: Event) {
        const editor = event.target as HTMLDivElement;
        this.setLastActiveEditor(editor);
        if (editor) {
            const sanitized = this.sanitizeHtml(editor.innerHTML);
            if (sanitized !== editor.innerHTML) {
                editor.innerHTML = sanitized;
                this.placeCursorAtEnd(editor);
            }
        }
        if (editor && this.getEditorText(editor).length > this.maxChars) {
            this.restoreLastValid(editor);
            return;
        }

        if (editor === ((this.$refs.welcomeEditor as HTMLDivElement | undefined) || null)) {
            this.welcomeMessageError = '';
        }
        if (editor === ((this.$refs.purchaseEditor as HTMLDivElement | undefined) || null)) {
            this.purchaseMessageError = '';
        }

        this.commitEditorState();
    }

    public onEditorSelectionEvent(event: Event) {
        this.setLastActiveEditor(event.target as HTMLDivElement);
        this.saveSelection();
        this.refreshToolbarState();
    }

    public onToolbarMouseDown() {
        this.saveSelection();
    }

    public onEditorBeforeInput(event: InputEvent) {
        const editor = event.target as HTMLDivElement;
        this.setLastActiveEditor(editor);
        if (!editor || !editor.isContentEditable) {
            return;
        }

        const inputType = event.inputType || '';
        if (inputType.startsWith('delete')) {
            return;
        }

        if (inputType === 'insertFromPaste') {
            return;
        }

        const incoming = event.data ?? (inputType === 'insertParagraph' ? '\n' : '');
        if (!incoming) {
            return;
        }

        const currentLength = this.getEditorText(editor).length;
        const selectedLength = this.getSelectionLengthWithin(editor);
        const nextLength = currentLength - selectedLength + incoming.length;
        if (nextLength > this.maxChars) {
            event.preventDefault();
            return;
        }

        // Keep chosen font size sticky while typing (collapsed selection case).
        if (this.activeFontSize) {
            this.prepareSelectionForCommand(editor);
            document.execCommand('fontSize', false, this.activeFontSize);
        }
    }

    public onEditorKeydown(event: KeyboardEvent) {
        const editor = event.target as HTMLDivElement;
        this.setLastActiveEditor(editor);
        if (!editor || !editor.isContentEditable) {
            return;
        }

        if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }

        if (event.key === 'Backspace' || event.key === 'Delete' || event.key.startsWith('Arrow') || event.key === 'Home' || event.key === 'End' || event.key === 'Tab' || event.key === 'Escape') {
            return;
        }

        if (event.key.length === 1 || event.key === 'Enter') {
            const currentLength = this.getEditorText(editor).length;
            const selectedLength = this.getSelectionLengthWithin(editor);
            const addedLength = event.key === 'Enter' ? 1 : event.key.length;
            if (currentLength - selectedLength + addedLength > this.maxChars) {
                event.preventDefault();
            }
        }
    }

    public onEditorPaste(event: ClipboardEvent) {
        const editor = event.target as HTMLDivElement;
        this.setLastActiveEditor(editor);
        if (!editor || !editor.isContentEditable) {
            return;
        }

        event.preventDefault();
        const pastedText = event.clipboardData?.getData('text/plain') || '';
        if (!pastedText) {
            return;
        }

        const currentLength = this.getEditorText(editor).length;
        const selectedLength = this.getSelectionLengthWithin(editor);
        const allowed = this.maxChars - (currentLength - selectedLength);
        if (allowed <= 0) {
            return;
        }

        const toInsert = pastedText.slice(0, allowed);
        document.execCommand('insertText', false, toInsert);
        this.commitEditorState();
    }

    public toggleCommand(command: 'bold' | 'italic' | 'underline') {
        const editor = this.activeEditor;
        if (!editor) {
            return;
        }

        this.prepareSelectionForCommand(editor);
        document.execCommand(command, false);
        this.commitEditorState();
    }

    public applyFontSize(size: '4' | '7') {
        const editor = this.activeEditor;
        if (!editor) {
            return;
        }

        this.activeFontSize = size;
        this.prepareSelectionForCommand(editor);
        document.execCommand('fontSize', false, size);
        this.commitEditorState();
    }

    public refreshToolbarState() {
        const editor = this.activeEditor;
        if (!editor) {
            return;
        }

        this.toolbarState.bold = !!document.queryCommandState('bold');
        this.toolbarState.italic = !!document.queryCommandState('italic');
        this.toolbarState.underline = !!document.queryCommandState('underline');
        this.toolbarState.large = this.activeFontSize === '7';
        this.toolbarState.small = this.activeFontSize === '4';
    }

    private saveSelection() {
        const editor = this.activeEditor;
        if (!editor) {
            return;
        }

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const range = selection.getRangeAt(0);
        if (editor.contains(range.startContainer) && editor.contains(range.endContainer)) {
            return;
        }
        this.placeCursorAtEnd(editor);
    }

    private commitEditorState() {
        this.syncModelFromEditors();
        this.saveSelection();
        this.refreshToolbarState();
        this.saveLastValidFromEditors();
    }

    private getEditorText(editor: HTMLDivElement) {
        return (editor.innerText || '')
            .replace(/\u200B/g, '')
            .replace(/\u00A0/g, ' ')
            .replace(/\r/g, '');
    }

    private sanitizeHtml(html: string) {
        const source = document.createElement('div');
        source.innerHTML = html || '';
        const target = document.createElement('div');

        this.sanitizeNodes(source, target);
        return target.innerHTML;
    }

    private sanitizeNodes(source: Node, targetParent: Node) {
        Array.from(source.childNodes).forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const cleanText = (node.textContent || '').replace(/\u200B/g, '');
                if (cleanText.length > 0) {
                    targetParent.appendChild(document.createTextNode(cleanText));
                }
                return;
            }

            if (node.nodeType !== Node.ELEMENT_NODE) {
                return;
            }

            const element = node as HTMLElement;
            const tag = element.tagName.toUpperCase();

            if (tag === 'BR') {
                targetParent.appendChild(document.createElement('br'));
                return;
            }

            if (tag === 'B' || tag === 'STRONG') {
                const b = document.createElement('b');
                this.sanitizeNodes(element, b);
                targetParent.appendChild(b);
                return;
            }

            if (tag === 'I' || tag === 'EM') {
                const i = document.createElement('i');
                this.sanitizeNodes(element, i);
                targetParent.appendChild(i);
                return;
            }

            if (tag === 'U') {
                const u = document.createElement('u');
                this.sanitizeNodes(element, u);
                targetParent.appendChild(u);
                return;
            }

            if (tag === 'FONT') {
                const size = element.getAttribute('size');
                const sizeAllowed = size === '4' || size === '7';
                if (sizeAllowed) {
                    const font = document.createElement('font');
                    font.setAttribute('size', size as string);
                    this.sanitizeNodes(element, font);
                    targetParent.appendChild(font);
                } else {
                    this.sanitizeNodes(element, targetParent);
                }
                return;
            }

            if (tag === 'DIV' || tag === 'P') {
                const block = document.createElement('div');
                this.sanitizeNodes(element, block);
                targetParent.appendChild(block);
                return;
            }

            if (tag === 'SPAN') {
                this.sanitizeSpan(element, targetParent);
                return;
            }

            this.sanitizeNodes(element, targetParent);
        });
    }

    private sanitizeSpan(element: HTMLElement, targetParent: Node) {
        const style = (element.getAttribute('style') || '').toLowerCase();
        const isBold = /font-weight\s*:\s*(bold|[6-9]00)/.test(style);
        const isItalic = /font-style\s*:\s*italic/.test(style);
        const isUnderline = /text-decoration[^:]*:\s*[^;]*underline/.test(style) ||
            /text-decoration-line\s*:\s*underline/.test(style);
        const mappedSize = this.mapSpanSize(style);

        const fragment = document.createDocumentFragment();
        this.sanitizeNodes(element, fragment);

        let wrapped: Node = fragment;
        if (mappedSize) {
            const font = document.createElement('font');
            font.setAttribute('size', mappedSize);
            font.appendChild(wrapped);
            wrapped = font;
        }
        if (isUnderline) {
            const u = document.createElement('u');
            u.appendChild(wrapped);
            wrapped = u;
        }
        if (isItalic) {
            const i = document.createElement('i');
            i.appendChild(wrapped);
            wrapped = i;
        }
        if (isBold) {
            const b = document.createElement('b');
            b.appendChild(wrapped);
            wrapped = b;
        }

        targetParent.appendChild(wrapped);
    }

    private mapSpanSize(style: string): '4' | '7' | null {
        if (!style) {
            return null;
        }

        const em = style.match(/font-size\s*:\s*([0-9.]+)em/);
        if (em) {
            return parseFloat(em[1]) >= 1.6 ? '7' : '4';
        }

        const px = style.match(/font-size\s*:\s*([0-9.]+)px/);
        if (px) {
            return parseFloat(px[1]) >= 24 ? '7' : '4';
        }

        if (/font-size\s*:\s*(x-large|xx-large|larger)/.test(style)) {
            return '7';
        }
        if (/font-size\s*:\s*(medium|small|smaller|large)/.test(style)) {
            return '4';
        }

        return null;
    }

    private htmlToPlainText(html: string) {
        const div = document.createElement('div');
        div.innerHTML = html || '';
        return (div.textContent || '')
            .replace(/\u200B/g, '')
            .replace(/\u00A0/g, ' ')
            .replace(/\r/g, '');
    }

    private getSelectionLengthWithin(editor: HTMLDivElement) {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return 0;
        }
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) {
            return 0;
        }
        return selection.toString().length;
    }

    private saveLastValidFromEditors() {
        const welcome = (this.$refs.welcomeEditor as HTMLDivElement | undefined) || null;
        const purchase = (this.$refs.purchaseEditor as HTMLDivElement | undefined) || null;
        if (welcome && this.getEditorText(welcome).length <= this.maxChars) {
            this.lastValidWelcomeHtml = welcome.innerHTML;
        }
        if (purchase && this.getEditorText(purchase).length <= this.maxChars) {
            this.lastValidPurchaseHtml = purchase.innerHTML;
        }
    }

    private restoreLastValid(editor: HTMLDivElement) {
        const isWelcome = editor === ((this.$refs.welcomeEditor as HTMLDivElement | undefined) || null);
        editor.innerHTML = isWelcome ? this.lastValidWelcomeHtml : this.lastValidPurchaseHtml;
        editor.focus();
        this.placeCursorAtEnd(editor);
        this.syncModelFromEditors();
        this.refreshToolbarState();
    }

    private placeCursorAtEnd(editor: HTMLElement) {
        const range = document.createRange();
        const selection = window.getSelection();
        if (!selection) {
            return;
        }
        range.selectNodeContents(editor);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private ensureEditorHasCaretAnchor(editor: HTMLElement) {
        const text = (editor.textContent || '').replace(/\u200B/g, '').trim();
        if (!text && editor.childNodes.length === 0) {
            editor.textContent = '\u200B';
        }
    }

    private focusActiveEditor() {
        const editor = this.activeEditor;
        if (!editor) {
            return;
        }
        this.ensureEditorHasCaretAnchor(editor);
        editor.focus();
        this.placeCursorAtEnd(editor);
    }

    private prepareSelectionForCommand(editor: HTMLDivElement) {
        this.ensureEditorHasCaretAnchor(editor);
        editor.focus();
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            this.placeCursorAtEnd(editor);
            this.saveSelection();
            return;
        }

        const range = selection.getRangeAt(0);
        const inEditor = editor.contains(range.startContainer) && editor.contains(range.endContainer);
        if (!inEditor) {
            this.placeCursorAtEnd(editor);
            this.saveSelection();
            return;
        }
    }

    private initializeActiveEditorState() {
        this.clearInitFocusRaf();
        this.syncEditorsFromModel();
        let attempts = 0;
        const tryFocus = () => {
            attempts += 1;
            this.focusActiveEditor();
            this.saveSelection();
            this.refreshToolbarState();
            this.saveLastValidFromEditors();

            const editor = this.activeEditor;
            const selection = window.getSelection();
            const ok = !!(
                editor &&
                selection &&
                selection.rangeCount > 0 &&
                editor.contains(selection.getRangeAt(0).startContainer)
            );
            if (!ok && attempts < 8) {
                this.initFocusRafId = requestAnimationFrame(tryFocus);
            } else {
                this.initFocusRafId = null;
            }
        };

        this.initFocusRafId = requestAnimationFrame(tryFocus);
    }

    private isPurchaseTab() {
        return Number(this.currentTab) === 1;
    }

    private clearInitFocusRaf() {
        if (this.initFocusRafId !== null) {
            cancelAnimationFrame(this.initFocusRafId);
            this.initFocusRafId = null;
        }
    }

    private setLastActiveEditor(editor: HTMLDivElement | null) {
        if (!editor || !editor.classList || !editor.classList.contains('rich-editor')) {
            return;
        }
        this.lastActiveEditor = editor;
    }

    private clearValidationErrors() {
        this.templateNameErrors = [];
        this.welcomeMessageError = '';
        this.purchaseMessageError = '';
    }
}
